import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import BrokerConnect from "./BrokerConnect";
import CreateStrategyForm from "./CreateStrategyForm";
import AddUser from "./AddUser";
import StrategyMonitor from "./StrategyMonitor";
import TradeHistory from "./TradeHistory";
import NotificationSettings from "./NotificationSettings";
import ThemeToggle from "./ThemeToggle";

// Four segment-scoped dashboards, all sharing the same strategy list/create/
// monitor flow -- only the underlying instrument universe and which
// options-only panels show up in the create form differ (see
// CreateStrategyForm's segment prop and instrument_resolver.py on the
// backend). Index Option and Stock Option used to be one combined "Option &
// Future" tab; they're split so index and single-stock underlyings don't
// share a picker.
const SEGMENTS = [
  { key: "INDEX_OPTIONS", label: "Index Option" },
  { key: "OPTIONS", label: "Stock Option" },
  { key: "MCX", label: "MCX Commodity" },
  { key: "STOCK", label: "Stock Trade" },
  { key: "OPTION_SELLING", label: "Option Selling" },
];

// How often to re-poll the instrument/LTP column while the strategy list is
// showing. Kept relaxed (not the 3s the live monitor uses) since this fires
// one quote request per listed strategy and Angel One rate-limits per
// account, not per strategy -- a long list polling too fast would compete
// with any strategies actually running.
const QUOTE_POLL_MS = 12000;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [segment, setSegment] = useState("INDEX_OPTIONS");
  const [strategies, setStrategies] = useState([]);
  const [quotes, setQuotes] = useState({}); // strategy id -> {tradingsymbol, ltp, close, error}
  const [view, setView] = useState("list"); // list | create | edit | addUser
  const [editing, setEditing] = useState(null);
  // One monitored strategy per tab, not one globally -- so Index Option can
  // be watching strategy A while you switch over and start strategy B on
  // MCX Commodity; both keep running server-side and each tab shows its own
  // live monitor (or stays blank) independently. Keyed by segment.
  const [monitoringBySegment, setMonitoringBySegment] = useState({});

  const setMonitoringFor = (segmentKey, strategyOrNull) => {
    setMonitoringBySegment((m) => {
      const next = { ...m };
      if (strategyOrNull) next[segmentKey] = strategyOrNull;
      else delete next[segmentKey];
      return next;
    });
  };

  const refresh = () => api.listStrategies(segment).then((list) => { setStrategies(list); return list; }).catch(() => []);

  const refreshQuotes = (list) => {
    if (!list || list.length === 0) return;
    Promise.all(
      list.map((s) =>
        api.strategyQuote(s.id)
          .then((q) => [s.id, q])
          .catch((err) => [s.id, { tradingsymbol: null, ltp: null, close: null, error: err.message }])
      )
    ).then((pairs) => setQuotes(Object.fromEntries(pairs)));
  };

  useEffect(() => {
    if (view !== "list") return;
    let cancelled = false;
    refresh().then((list) => { if (!cancelled) refreshQuotes(list); });
    const interval = setInterval(() => { refresh().then((list) => { if (!cancelled) refreshQuotes(list); }); }, QUOTE_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, view]);

  const openTab = (key) => {
    setSegment(key);
    setView("list");
    setEditing(null);
    // monitoringBySegment is keyed by tab, so switching tabs naturally shows
    // whatever that tab is monitoring (or nothing) without touching state
    // here -- no more wiping a still-running strategy's monitor just because
    // you looked at a different tab.
  };
  const openCreate = () => { setEditing(null); setView("create"); };
  const openEdit = (s) => { setEditing(s); setView("edit"); };
  const closeForm = () => { setView("list"); refresh().then(refreshQuotes); };
  const remove = async (id) => {
    if (!window.confirm("Delete this strategy?")) return;
    await api.deleteStrategy(id);
    refresh().then(refreshQuotes);
  };

  const activeSegment = SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0];

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.brand}>AlgoDesk</div>
        <nav style={styles.nav}>
          {SEGMENTS.map((s) => (
            <span
              key={s.key}
              style={styles.navItem(view !== "addUser" && view !== "history" && view !== "alerts" && segment === s.key)}
              onClick={() => openTab(s.key)}
            >
              {s.label}
            </span>
          ))}
          <span style={styles.navItem(view === "history")} onClick={() => setView("history")}>Trade History</span>
          <span style={styles.navItem(view === "alerts")} onClick={() => setView("alerts")}>Alerts</span>
          <span style={styles.navItem(view === "addUser")} onClick={() => setView("addUser")}>Users</span>
        </nav>
        <div style={styles.userBox}>
          <ThemeToggle style={{ position: "static" }} />
          <span style={{ color: "var(--text-dim)", fontSize: 12 }}>{user.fullName || user.username}</span>
          <button style={styles.logoutBtn} onClick={logout}>Log Out</button>
        </div>
      </header>

      <BrokerConnect />

      {(view === "create" || view === "edit") && (
        <CreateStrategyForm existing={editing} segment={segment} onSaved={closeForm} onCancel={closeForm} />
      )}

      {view === "addUser" && (
        <div style={{ padding: 24 }}>
          <AddUser embedded />
        </div>
      )}

      {view === "history" && (
        <div style={{ padding: 20 }}>
          <TradeHistory />
        </div>
      )}

      {view === "alerts" && (
        <div style={{ padding: 20 }}>
          <NotificationSettings />
        </div>
      )}

      {view === "list" && (
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{activeSegment.label} — Your Strategies</div>
            <button style={styles.newBtn} onClick={openCreate}>+ Create Strategy</button>
          </div>

          {strategies.length === 0 ? (
            <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
              No {activeSegment.label.toLowerCase()} strategies yet. Click "Create Strategy" to set one up.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Alias</th>
                  <th style={styles.th}>Underlying</th>
                  <th style={styles.th}>Instrument</th>
                  <th style={styles.th}>LTP</th>
                  <th style={styles.th}>Prev Close</th>
                  <th style={styles.th}>Updated</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {strategies.map((s) => {
                  const q = quotes[s.id];
                  return (
                    <tr key={s.id}>
                      <td style={styles.td}>{s.alias_name || "(unnamed)"}</td>
                      <td style={styles.td}>{s.underlying}</td>
                      <td style={{ ...styles.td, fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
                        {q?.tradingsymbol || <span style={{ color: "var(--text-dim)" }}>{q?.error || "…"}</span>}
                      </td>
                      <td style={{ ...styles.td, fontFamily: "var(--font-mono)" }}>
                        {q?.ltp != null ? Number(q.ltp).toFixed(2) : <span style={{ color: "var(--text-dim)" }}>—</span>}
                      </td>
                      <td style={{ ...styles.td, fontFamily: "var(--font-mono)", color: "var(--text-dim)" }}>
                        {q?.close != null ? Number(q.close).toFixed(2) : "—"}
                      </td>
                      <td style={styles.td}>{new Date(s.updated_at).toLocaleString()}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        {s.running && <span style={styles.liveDot} title="Running">●</span>}
                        <button
                          style={styles.linkBtn}
                          onClick={() => setMonitoringFor(s.segment || segment, s)}
                          title={s.running ? "Open the live monitor for this running strategy" : "Open the monitor to run this strategy"}
                        >
                          {s.running ? "Running" : "Run"}
                        </button>
                        <button style={styles.linkBtn} onClick={() => openEdit(s)}>Edit</button>
                        <button style={styles.linkBtnDanger} onClick={() => remove(s.id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {monitoringBySegment[segment] && (
        <StrategyMonitor
          strategy={monitoringBySegment[segment]}
          onClose={() => setMonitoringFor(segment, null)}
          onStopped={() => refresh().then(refreshQuotes)}
        />
      )}
    </div>
  );
}

const styles = {
  app: { minHeight: "100vh", background: "var(--bg)" },
  header: {
    display: "flex", alignItems: "center", gap: 24, padding: "10px 16px",
    borderBottom: "1px solid var(--border)", background: "var(--panel)",
  },
  brand: { fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 15 },
  nav: { display: "flex", gap: 18, flex: 1 },
  navItem: (active) => ({
    fontSize: 12.5, cursor: "pointer", color: active ? "var(--text)" : "var(--text-dim)",
    fontWeight: active ? 600 : 400, borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
    paddingBottom: 3, whiteSpace: "nowrap",
  }),
  userBox: { display: "flex", alignItems: "center", gap: 10 },
  logoutBtn: {
    background: "var(--panel-alt)", border: "1px solid var(--border)", color: "var(--text)",
    borderRadius: 5, padding: "5px 10px", fontSize: 11, cursor: "pointer",
  },
  newBtn: {
    background: "var(--accent)", color: "#04140a", border: "none", borderRadius: 6,
    padding: "8px 16px", fontWeight: 600, fontSize: 12.5, cursor: "pointer",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", color: "var(--text-dim)", fontWeight: 500, padding: "8px 10px", borderBottom: "1px solid var(--border)" },
  td: { padding: "9px 10px", borderBottom: "1px solid var(--border)" },
  linkBtn: { background: "none", border: "none", color: "var(--focus)", cursor: "pointer", fontSize: 12, marginRight: 10 },
  linkBtnDisabled: { background: "none", border: "none", color: "var(--text-dim)", cursor: "not-allowed", fontSize: 12, marginRight: 10, opacity: 0.7 },
  linkBtnDanger: { background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 12 },
  liveDot: { color: "var(--accent)", fontSize: 9, marginRight: 5, verticalAlign: "middle" },
};
